import { useId, useRef, useState } from 'react';
import { isThemePreview } from '../../api/previewVisit';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { OrderStatusCard } from './OrderStatusCard';
import { ORDER_LOOKUP_WORDS, type OrderLookupWords } from './orderLookupWords';
import { sampleTrackedOrder } from './sampleTrackedOrder';
import { useOrderLookup, type LookupState } from './useOrderLookup';
import './elementOrderLookup.css';

// A shopper's way to their own order without an account: the number from their confirmation and the phone
// they ordered with, then the order itself — how far it has come, its parcel, its lines. The fields keep
// what was typed through a miss, and a confirmation's "Track your order" link arrives with the number in.
// The builder and a preview show a sample order under the form, so the card can be styled.

/** What the form holds and how it reports a lookup. */
type FormProps = { words: OrderLookupWords; state: LookupState; onFind: (orderNumber: string, phone: string) => void };

// Reads the order number a confirmation's link carries, so the shopper only types their phone.
const numberInAddress = (): string => new URLSearchParams(window.location.search).get('order')?.slice(0, 50) ?? '';

// Says why the last lookup found nothing, or nothing while there is no such answer.
function failureText(state: LookupState, words: OrderLookupWords): string {
  if (state.status === 'missing' || state.status === 'failed' || state.status === 'tooMany') return words[state.status];
  return '';
}

// Draws the two fields and the button, with the reason a lookup found nothing under them.
const LookupForm: React.FC<FormProps> = ({ words, state, onFind }) => {
  const id = useId();
  const [orderNumber, setOrderNumber] = useState(numberInAddress);
  const [phone, setPhone] = useState('');
  const busy = state.status === 'busy';
  const failure = failureText(state, words);
  return (
    <form className="xe-track-form" aria-describedby={`${id}-hint`} onSubmit={(event) => { event.preventDefault(); onFind(orderNumber.trim(), phone.trim()); }}>
      <label htmlFor={`${id}-number`}>{words.orderNumber}</label>
      <input id={`${id}-number`} name="orderNumber" required maxLength={50} autoComplete="off" placeholder="#1001" value={orderNumber} onChange={(event) => setOrderNumber(event.target.value)} />
      <label htmlFor={`${id}-phone`}>{words.phone}</label>
      <input id={`${id}-phone`} name="phone" type="tel" required minLength={6} maxLength={30} autoComplete="tel" dir="ltr" value={phone} onChange={(event) => setPhone(event.target.value)} />
      <p id={`${id}-hint`} className="xe-track-hint">{words.hint}</p>
      {failure && <p className="xe-track-error" role="alert">{failure}</p>}
      <button type="submit" disabled={busy} aria-busy={busy}>{busy ? words.finding : words.find}</button>
    </form>
  );
};

// Draws the lookup, and under it the order it found, or a sample order in the builder and a preview.
export const OrderLookupElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const { store, editing } = useElementRender();
  const words = ORDER_LOOKUP_WORDS[store?.language ?? 'en'];
  const { state, find, reset } = useOrderLookup();
  const found = state.status === 'found' ? state.order : null;
  const sample = !found && (editing || isThemePreview()) ? sampleTrackedOrder() : null;
  const form = useRef<HTMLDivElement>(null);
  // Goes back to the form, its fields as they were, with the keyboard in the first one.
  const again = () => { reset(); requestAnimationFrame(() => form.current?.querySelector('input')?.focus()); };
  return (
    <div {...box} className={[box.className, 'xe-track-lookup'].filter(Boolean).join(' ')}>
      <div ref={form} hidden={found !== null}><LookupForm words={words} state={state} onFind={(orderNumber, phone) => void find(orderNumber, phone)} /></div>
      {found && <OrderStatusCard order={found} words={words} store={store} onAnother={again} />}
      {sample && <OrderStatusCard order={sample} words={words} store={store} />}
    </div>
  );
};
